(function() {
const themeDebounce =
  window.themeCore?.debounce ||
  window.debounce ||
  function debounceFallback(fn, wait) {
    let timeout;
    return function debouncedFallback(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  };

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 768px)');
        this.thumbnailsInitialized = false;
      }

      connectedCallback() {
        // Initialize after element is connected to DOM
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', themeDebounce(this.onSlideChanged.bind(this), 500));
        }
        
        // Initialize thumbnail buttons - use multiple attempts to ensure DOM is ready
        let initAttempts = 0;
        const initThumbnailsWithRetry = () => {
          const thumbnailList = this.querySelector('[id^="Slider-Thumbnails"]');
          const buttons = thumbnailList ? thumbnailList.querySelectorAll('.thumbnail') : [];
          
          if (thumbnailList && buttons.length > 0) {
            this.initThumbnails();
            this.updateThumbnails();
          } else if (initAttempts < 30) {
            initAttempts++;
            setTimeout(initThumbnailsWithRetry, 150);
          }
        };
        
        setTimeout(initThumbnailsWithRetry, 200);
        
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      disconnectedCallback() {
        // Cleanup observer if exists
        if (this._thumbnailObserver) {
          this._thumbnailObserver.disconnect();
          this._thumbnailObserver = null;
        }
        const thumbnailList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbnailList && this._thumbnailHandlers) {
          thumbnailList.removeEventListener('click', this._thumbnailHandlers.listClickHandler, true);
          thumbnailList.removeEventListener('touchend', this._thumbnailHandlers.listTouchHandler, true);
          thumbnailList.removeEventListener('mousedown', this._thumbnailHandlers.mousedownHandler, true);
          this._thumbnailHandlers = null;
        }
        this.thumbnailsInitialized = false;
      }

      initThumbnails() {
        if (this.thumbnailsInitialized) return;
        
        const thumbnailList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (!thumbnailList) {
          setTimeout(() => this.initThumbnails(), 200);
          return;
        }

        this.thumbnailsInitialized = true;

        // Function to get media ID from button or list item
        const getMediaId = (button) => {
          if (!button) return null;
          let mediaId = button.dataset.mediaId;
          if (!mediaId) {
            const listItem = button.closest('.thumbnail-list__item');
            if (listItem) {
              mediaId = listItem.getAttribute('data-target');
            }
          }
          return mediaId;
        };

        // Direct click handler - simple and reliable
        const handleThumbnailClick = (e) => {
          // Find the button
          let button = e.target.closest('.thumbnail');
          if (!button) {
            const listItem = e.target.closest('.thumbnail-list__item');
            if (listItem) {
              button = listItem.querySelector('.thumbnail');
            }
          }
          
          if (!button) return;
          
          const mediaId = getMediaId(button);
          if (!mediaId) return;
          
          // Stop all event propagation immediately
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Call setActiveMedia directly
          this.setActiveMedia(mediaId);
        };

        const listClickHandler = (e) => {
          if (e.target.closest('.thumbnail')) handleThumbnailClick(e);
        };
        const listTouchHandler = (e) => {
          if (e.target.closest('.thumbnail')) handleThumbnailClick(e);
        };
        const mousedownHandler = (e) => {
          if (e.target.closest('.thumbnail')) e.stopPropagation();
        };

        thumbnailList.addEventListener('click', listClickHandler, true);
        thumbnailList.addEventListener('touchend', listTouchHandler, { passive: false, capture: true });
        thumbnailList.addEventListener('mousedown', mousedownHandler, true);
        this._thumbnailHandlers = { listClickHandler, listTouchHandler, mousedownHandler };
      }

      updateThumbnails() {
        const thumbnailList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (!thumbnailList) return;

        const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]');
        if (!activeMedia) return;

        const activeMediaId = activeMedia.dataset.mediaId;
        const thumbnailButtons = thumbnailList.querySelectorAll('.thumbnail');
        
        thumbnailButtons.forEach((button) => {
          if (button.dataset.mediaId === activeMediaId) {
            button.setAttribute('aria-current', 'true');
            button.classList.add('thumbnail--active');
            // Scroll active thumbnail into view
            const thumbnailItem = button.closest('.thumbnail-list__item');
            if (thumbnailItem && thumbnailItem.scrollIntoView) {
              thumbnailItem.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'nearest', inline: 'center' });
            }
          } else {
            button.removeAttribute('aria-current');
            button.classList.remove('thumbnail--active');
          }
        });
      }

      onSlideChanged(event) {
        if (event.detail && event.detail.currentElement) {
          const currentElement = event.detail.currentElement;
          if (currentElement.dataset && currentElement.dataset.mediaId) {
            // Prevent resetting to the first image if a variant was selected
            if (this.elements.viewer.dataset.selectedMediaId && 
                this.elements.viewer.dataset.selectedMediaId !== currentElement.dataset.mediaId) {
                return;
            }
            if (this.elements.thumbnails) {
              const thumbnail = this.elements.thumbnails.querySelector(
                  `[data-target="${currentElement.dataset.mediaId}"]`
              );
              if (thumbnail) {
                this.setActiveThumbnail(thumbnail);
              }
            }
            // Update thumbnails
            this.updateThumbnails();
          }
        }
      }
      
      setActiveMedia(mediaId) {
        const exactMatch = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const fallbackMatch = this.elements.viewer.querySelector('[data-media-id]');
        const activeMedia = exactMatch || fallbackMatch;
        
        if (!activeMedia) {
          return;
        }
        
        // Remove is-active from all
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Add is-active to target
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Always scroll the main gallery to show the active image
          const slider = activeMedia.parentElement;
          if (slider && slider.scrollTo) {
            slider.scrollTo({ 
              left: activeMedia.offsetLeft - slider.offsetLeft, 
              behavior: this.getScrollBehavior()
            });
          }
          
          // Also scroll page if image is not in viewport
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top < 0 || activeMediaRect.bottom > window.innerHeight) {
            const top = activeMediaRect.top + window.scrollY - 100; // 100px offset from top
            window.scrollTo({ top: top, behavior: this.getScrollBehavior() });
          }
        }, 50);
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
        }
        this.elements.viewer.dataset.selectedMediaId = mediaId;
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        
        // Update thumbnails
        this.updateThumbnails();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Update all thumbnail buttons
        const thumbnailList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbnailList) {
          thumbnailList.querySelectorAll('.thumbnail').forEach((button) => {
            button.removeAttribute('aria-current');
            button.classList.remove('thumbnail--active');
          });
        }

        const thumbnailButton = thumbnail.querySelector('button');
        if (thumbnailButton) {
          thumbnailButton.setAttribute('aria-current', 'true');
          thumbnailButton.classList.add('thumbnail--active');
        }

        // Scroll thumbnail into view if needed
        if (this.elements.thumbnails && this.elements.thumbnails.isSlideVisible) {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          this.elements.thumbnails.setSlidePosition(thumbnail[this.elements.thumbnails.offsetPosition]);
        } else if (thumbnail.scrollIntoView) {
          thumbnail.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'nearest', inline: 'center' });
        }
      }

      getScrollBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        const imageAvailableMessage = window.accessibilityStrings?.imageAvailable;
        if (!image || !this.elements.liveRegion || !imageAvailableMessage) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = imageAvailableMessage.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// Global fallback: ensure thumbnail clicks always activate media
document.addEventListener(
  'click',
  (e) => {
    const button = e.target.closest('.thumbnail');
    if (!button) return;

    const listItem = button.closest('.thumbnail-list__item');
    const mediaId = button.getAttribute('data-media-id') || (listItem ? listItem.getAttribute('data-target') : null);
    if (!mediaId) return;

    const gallery = button.closest('media-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      e.preventDefault();
      e.stopPropagation();
      gallery.setActiveMedia(mediaId);
    }
  },
  true
);
})();
